/* eslint-disable @typescript-eslint/strict-boolean-expressions */
// istanbul ignore file
import interceptedAxios from '../../../utils/interceptedAxios'
import { z } from 'zod'

const ZRead = z.object({
  readingTime: z.string().nullable(),
  value: z.number().nullable(),
  indexValue: z.number().nullable()
})

const ZReadingcimCodeIds = z.object({
  assetId: z.string(),
  cimCodeId: z.string(),
  units: z.string().nullable(),
  errorMessage: z.string().optional(),
  totalCount: z.number(),
  registerName: z.string().optional(),
  assetIntervalReadingsData: z.array(ZRead)
})

const ZReadingCimCodeData = z.array(ZReadingcimCodeIds)

const ZIntervalReadingData = z.object({
  assetId: z.string(),
  totalCount: z.number(),
  errorMessage: z.string().optional(),
  assetIntervalReadingsData: z.array(ZReadingcimCodeIds)
})

const ZIntervalReading = z.object({
  error: z.boolean(),
  http_status: z.number(),
  data: ZIntervalReadingData
})

export type intervalData = z.infer<typeof ZReadingcimCodeIds>
interface IntervalDataItem {
  cimCodeId: string
}

const separateIntervalData = (
  intervalData: IntervalDataItem[],
  selectedCimCode1: string,
  selectedCimCode2: string,
  selectedCimCode3: string,
  selectedCimCode4: string
): Record<string, IntervalDataItem[]> => {
  const selectedCimCodes = [selectedCimCode1, selectedCimCode2, selectedCimCode3, selectedCimCode4]
  const result: Record<string, IntervalDataItem[]> = {
    ModBusParameter1: [],
    ModBusParameter2: [],
    ModBusParameter3: [],
    ModBusParameter4: []
  }

  // Check if all selected CIM codes are empty or null
  const allCimCodesEmpty = selectedCimCodes.every(cimCode => !cimCode)

  if (allCimCodesEmpty) {
    // If all CIM codes are empty or null, put all interval data under each ModBusParameter
    intervalData.forEach((data, index) => {
      result[`ModBusParameter${index + 1}`] = [data]
    })
  } else {
    // Filter and categorize interval data based on CIM codes
    intervalData.forEach((data) => {
      const index = selectedCimCodes.indexOf(data.cimCodeId)
      if (index !== -1) {
        result[`ModBusParameter${index + 1}`].push(data)
      }
    })
  }
  return result
}

export type flameReadingData = z.infer<typeof ZIntervalReading>
export type ModBusParameter1 = z.infer<typeof ZReadingCimCodeData>
export type ModBusParameter2 = z.infer<typeof ZReadingCimCodeData>
export type ModBusParameter3 = z.infer<typeof ZReadingCimCodeData>
export type ModBusParameter4 = z.infer<typeof ZReadingCimCodeData>

export async function getFlameSignaGraphData (
  assetId: string,
  cimCodeIds: string,
  startDateTime: string,
  endDateTime: string,
  graphName: string,
  onSuccess: (ModBusParameter1: ModBusParameter1, ModBusParameter2: ModBusParameter2, ModBusParameter3: ModBusParameter3, ModBusParameter4: ModBusParameter4, httpStatus: number) => any,
  onFailure: (errorMessage: string, httpStatus: number) => any
): Promise<void> {
  const cimCodeIdsArray = cimCodeIds.split(',').map(id => id.trim())
  const url = `/asset-data-service/api/v1/udp/tiq/assets/data/interval/multiple/tiqGetIntervalReadings?assetId=${assetId}&cimCodeIds=${cimCodeIds.toString()}&startDateTime=${startDateTime}&endDateTime=${endDateTime}&graphName=${graphName}`
  interceptedAxios.get(url, { timeout: 10000 })
    .then((response) => {
      if (response.status === 200) {
        const parseResponse = ZIntervalReadingData.safeParse(response.data.data)
        if (parseResponse.success) {
          if (response.data.data !== null && response.data.data !== undefined) {
            const separatedData = separateIntervalData(
              parseResponse.data.assetIntervalReadingsData,
              cimCodeIdsArray[0] || '', // Default to empty string if undefined
              cimCodeIdsArray[1] || '', // Default to empty string if undefined
              cimCodeIdsArray[2] || '', // Default to empty string if undefined
              cimCodeIdsArray[3] || '' // Default to empty string if undefined
            )
            const ModBusParameter1 = ZReadingCimCodeData.safeParse(separatedData.ModBusParameter1)
            const ModBusParameter2 = ZReadingCimCodeData.safeParse(separatedData.ModBusParameter2)
            const ModBusParameter3 = ZReadingCimCodeData.safeParse(separatedData.ModBusParameter3)
            const ModBusParameter4 = ZReadingCimCodeData.safeParse(separatedData.ModBusParameter4)
            if (ModBusParameter1.success && ModBusParameter2.success && ModBusParameter3.success && ModBusParameter4.success) {
              onSuccess(ModBusParameter1.data, ModBusParameter2.data, ModBusParameter3.data, ModBusParameter4.data, response.status)
            } else {
              // Handle parsing failure
              onFailure('Failed to parse ModBusParameters', response.status)
            }
          }
        } else {
          onFailure(parseResponse.error.message, response.status)
        }
      }
    })
    .catch((error) => {
      if (error.response !== undefined) {
        if (error.response.data !== undefined) {
          onFailure(error.response.data.error_message, error.response.status)
        } else {
          onFailure(error.message, error.response.status)
        }
      } else {
        onFailure(error.message, error.status)
      }
    })
}
